/**
 * Embedded Signup CoEx (Meta WhatsApp Cloud API) — utilitário compartilhado.
 *
 * Centraliza o carregamento do JS SDK do Facebook e o disparo do fluxo de
 * Embedded Signup (variante coexistence), evitando duplicação entre a página
 * /whatsapp/meta e o modal de integração no Perfil.
 *
 * Config via env (Vite):
 *   VITE_META_APP_ID    : App ID público do app Meta.
 *   VITE_META_CONFIG_ID : id da configuração de Embedded Signup (coexistence).
 */

const FB_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js'
const META_APP_ID = import.meta.env.VITE_META_APP_ID
const META_CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID
const GRAPH_VERSION = 'v21.0'

// Carrega o SDK uma única vez e resolve quando window.FB existe.
let sdkPromise = null
export function loadFacebookSdk() {
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise((resolve, reject) => {
    if (window.FB) return resolve(window.FB)
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        autoLogAppEvents: true,
        xfbml: false,
        version: GRAPH_VERSION,
      })
      resolve(window.FB)
    }
    const script = document.createElement('script')
    script.src = FB_SDK_SRC
    script.async = true
    script.defer = true
    script.crossOrigin = 'anonymous'
    script.onerror = () => reject(new Error('Falha ao carregar o SDK do Facebook.'))
    document.body.appendChild(script)
  })
  return sdkPromise
}

export function isMetaSignupConfigured() {
  return Boolean(META_APP_ID && META_CONFIG_ID)
}

/**
 * Dispara o Embedded Signup CoEx e resolve com { code, wabaId, phoneNumberId }.
 *
 * Coordena duas fontes assíncronas:
 *  - postMessage WA_EMBEDDED_SIGNUP → waba_id + phone_number_id;
 *  - callback do FB.login → code OAuth.
 * Rejeita se o usuário cancela ou se faltar algum dado.
 */
export async function launchEmbeddedSignup() {
  if (!isMetaSignupConfigured()) {
    throw new Error('VITE_META_APP_ID / VITE_META_CONFIG_ID não configurados.')
  }
  const FB = await loadFacebookSdk()

  return new Promise((resolve, reject) => {
    const session = { wabaId: null, phoneNumberId: null }

    function onMessage(event) {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) {
        return
      }
      let data
      try {
        data = JSON.parse(event.data)
      } catch {
        return // mensagens não-JSON do SDK
      }
      if (data.type !== 'WA_EMBEDDED_SIGNUP') return
      if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA') {
        session.wabaId = data.data?.waba_id || null
        session.phoneNumberId = data.data?.phone_number_id || null
      }
    }
    window.addEventListener('message', onMessage)

    const cleanup = () => window.removeEventListener('message', onMessage)

    FB.login(
      (response) => {
        const code = response?.authResponse?.code
        cleanup()
        if (!code) {
          reject(new Error('Onboarding cancelado ou sem autorização.'))
          return
        }
        if (!session.wabaId || !session.phoneNumberId) {
          reject(new Error('Não foi possível obter o número (waba_id/phone_number_id).'))
          return
        }
        resolve({
          code,
          wabaId: session.wabaId,
          phoneNumberId: session.phoneNumberId,
        })
      },
      {
        config_id: META_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          // CoEx: onboarding de número que já roda no WhatsApp Business App.
          // featureType DEVE bater com a configuração de Embedded Signup do painel.
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        },
      }
    )
  })
}
